/**
 * Bevestigingsvraag in de app zelf, als vervanger van `window.confirm`.
 *
 * Reden: iOS/Safari toont na een paar vragen "Dialoogvensters onderdrukken" als
 * derde knop; wie die aantikt, krijgt daarna nooit meer een vraag en verwijderen
 * lijkt dan stuk. Deze vraag heeft precies twee knoppen, grote tikvlakken (pen/
 * touch) en werkt ook in een WebView. Imperatief: `await confirmDialog({...})`.
 */
import i18n from "i18next";
import { createRoot } from "react-dom/client";

export interface ConfirmDialogOptions {
  /** Meerregelige tekst; regels worden onder elkaar getoond. */
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  /** Rode bevestigknop (verwijderen). */
  danger?: boolean;
}

export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;
    const finish = (v: boolean) => {
      if (done) return;
      done = true;
      window.removeEventListener("keydown", onKey, true);
      root.unmount();
      host.remove();
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    const en = (i18n.language ?? "nl").startsWith("en");
    const [title, ...rest] = opts.message.split("\n");
    root.render(
      <div
        className="ledger fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
        role="alertdialog"
        aria-modal="true"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-md rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl">
          <p className="font-semibold text-(--ld-text) text-base">{title}</p>
          {rest.some((l) => l.trim() !== "") && (
            <div className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap text-(--ld-muted) text-sm">
              {rest.join("\n").trim()}
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="min-h-11 rounded border border-(--ld-control) bg-(--ld-surface) px-4 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
              onClick={() => finish(false)}
            >
              {opts.cancelLabel ?? (en ? "Cancel" : "Annuleren")}
            </button>
            <button
              type="button"
              className={`min-h-11 rounded border border-transparent px-4 font-medium text-sm ${
                opts.danger
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-(--ld-positive-bg) text-(--ld-positive-ink)"
              }`}
              onClick={() => finish(true)}
            >
              {opts.okLabel ?? (opts.danger ? (en ? "Delete" : "Verwijderen") : "OK")}
            </button>
          </div>
        </div>
      </div>,
    );
  });
}
